// ============================================================
// LinuxUsersGroups.jsx
//
// A branded, responsive course content page for the
// "Linux Users & Groups" lesson.
//
// Brand: ProDev Linux Series
// Fixed: Defined 'showScroll' state and scroll listener.
// ============================================================

import React, { useState, useEffect } from "react";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Collapse,
    Paper,
    Fab,
    Zoom,
    Stack
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import TerminalIcon           from "@mui/icons-material/Terminal";
import PeopleIcon             from "@mui/icons-material/People";
import PersonIcon             from "@mui/icons-material/Person";
import GroupAddIcon           from "@mui/icons-material/GroupAdd";
import FolderSharedIcon       from "@mui/icons-material/FolderShared";
import SecurityIcon           from "@mui/icons-material/Security";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LinkIcon               from "@mui/icons-material/Link";
import ExpandMoreIcon         from "@mui/icons-material/ExpandMore";
import ExpandLessIcon         from "@mui/icons-material/ExpandLess";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyIcon                from "@mui/icons-material/Key";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function ConceptCard({ icon, title, description }) {
    return (
        <Paper elevation={0} sx={{
            p: 2.5, height: "100%", background: BRAND.surface,
            border: `1px solid ${BRAND.border}`, borderRadius: 3,
            transition: "0.25s", "&:hover": { borderColor: BRAND.borderHover, boxShadow: `0 0 20px ${BRAND.goldGlow}` },
        }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.2 }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 22 } })}
                <Typography sx={{ fontFamily: "'Georgia', serif", fontWeight: 700, color: BRAND.white, fontSize: 14 }}>
                    {title}
                </Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.65 }}>
                {description}
            </Typography>
        </Paper>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 2 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy commands"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {comment && <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.3 }}># {comment}</Typography>}
                        <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 13, md: 14 }, color: BRAND.codeText }}>{cmd}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

function ExpandableExample({ title, icon, children }) {
    const [open, setOpen] = useState(false);
    return (
        <Box sx={{ border: `1px solid ${open ? BRAND.borderHover : BRAND.border}`, borderRadius: 2.5, overflow: "hidden", mb: 1.5 }}>
            <Box onClick={() => setOpen(!open)} sx={{ px: 2.5, py: 1.8, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", background: open ? BRAND.goldGlow : BRAND.surface }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
                    <Typography sx={{ color: BRAND.white, fontWeight: 600, fontSize: 14 }}>{title}</Typography>
                </Box>
                {open ? <ExpandLessIcon sx={{ color: BRAND.gold }} /> : <ExpandMoreIcon sx={{ color: BRAND.muted }} />}
            </Box>
            <Collapse in={open}>
                <Box sx={{ px: 2.5, py: 2, background: "rgba(0,0,0,0.25)", borderTop: `1px solid ${BRAND.border}` }}>{children}</Box>
            </Collapse>
        </Box>
    );
}

function ResourceLink({ href, label, sub }) {
    return (
        <Box component="a" href={href} target="_blank" rel="noreferrer" sx={{
            display: "flex", alignItems: "center", gap: 2, p: 2, mb: 1.2, textDecoration: "none",
            border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.surface, transition: "0.25s",
            "&:hover": { borderColor: BRAND.borderHover, boxShadow: `0 0 16px ${BRAND.goldGlow}` },
        }}>
            <LinkIcon sx={{ color: BRAND.gold, fontSize: 20, flexShrink: 0 }} />
            <Box><Typography sx={{ color: BRAND.gold, fontWeight: 700, fontSize: 14 }}>{label}</Typography>
                {sub && <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>{sub}</Typography>}</Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function LinuxUsersGroups() {
    // ✅ Fix: Defining the missing 'showScroll' state
    const [showScroll, setShowScroll] = useState(false);

    // ✅ Fix: Added listener to update scroll visibility
    useEffect(() => {
        const checkScroll = () => {
            if (!showScroll && window.pageYOffset > 400) {
                setShowScroll(true);
            } else if (showScroll && window.pageYOffset <= 400) {
                setShowScroll(false);
            }
        };

        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, [showScroll]);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Security · Module 03"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Linux <span>Users & Groups</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Linux is built from the ground up as a multi-user system. Managing who can access what is the foundation of system security and collaboration in professional environments.
                    </Typography>
                </Box>

                {/* OVERVIEW */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<PeopleIcon />} label="System Overview" />
                    <Typography sx={{ color: BRAND.muted, lineHeight: 1.8, fontSize: { xs: 13, md: 15 } }}>
                        Every process in Linux is owned by a <strong style={{ color: BRAND.white }}>User</strong>.
                        Users are organized into <strong style={{ color: BRAND.white }}>Groups</strong> to simplify permission
                        management. Whether you are adding a new developer to a project or restricting access to
                        sensitive logs, understanding the UID (User ID) and GID (Group ID) system is vital.
                    </Typography>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* KEY CONCEPTS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SecurityIcon />} label="Security Concepts" />
                    <Grid container spacing={2}>
                        {[
                            { icon: <PersonIcon />, title: "UID (User ID)", description: "A unique numerical ID assigned to every user. Root is always UID 0." },
                            { icon: <GroupAddIcon />, title: "GID (Group ID)", description: "Identifies a primary group. Users can also belong to multiple secondary groups." },
                            { icon: <KeyIcon />, title: "Sudo Rights", description: "The 'sudoers' file determines which users can execute commands as the superuser (root)." },
                        ].map((card) => (
                            <Grid item xs={12} md={4} key={card.title}><ConceptCard {...card} /></Grid>
                        ))}
                    </Grid>
                </Box>

                {/* TRY IT YOURSELF */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TerminalIcon />} label="User & Group Management" />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>👤 User Operations</Typography>
                    <CodeBlock lines={[
                        { cmd: "$ whoami", comment: "Display the current effective username" },
                        { cmd: "$ id", comment: "Show UID, GID, and all group memberships" },
                        { cmd: "$ sudo adduser hazzan", comment: "Interactive command to create user 'hazzan'" },
                        { cmd: "$ sudo passwd hazzan", comment: "Update password for the user" },
                        { cmd: "$ sudo deluser hazzan", comment: "Remove user account from the system" },
                    ]} />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>👥 Group Operations</Typography>
                    <CodeBlock lines={[
                        { cmd: "$ sudo groupadd prodevconsult", comment: "Create a new architectural group" },
                        { cmd: "$ sudo usermod -aG prodevconsult hazzan", comment: "Append user 'hazzan' to the group" },
                        { cmd: "$ groups hazzan", comment: "List all groups 'hazzan' belongs to" },
                    ]} />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 2, mt: 4 }}>🔽 Critical Configuration Files</Typography>
                    <ExpandableExample title="Where user data lives" icon={<FolderSharedIcon />}>
                        <Grid container spacing={2}>
                            {[
                                { file: "/etc/passwd", desc: "Contains user account info (shell, home dir, UID)." },
                                { file: "/etc/shadow", desc: "Stores encrypted passwords and aging info." },
                                { file: "/etc/group", desc: "Defines groups and lists their members." },
                            ].map(item => (
                                <Grid item xs={12} key={item.file}>
                                    <Typography sx={{ color: BRAND.goldBright, fontFamily: "monospace", fontSize: 13 }}>{item.file}</Typography>
                                    <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>{item.desc}</Typography>
                                </Grid>
                            ))}
                        </Grid>
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* LEARNED CHECKLIST */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Module Outcomes" />
                    <Grid container spacing={1.5}>
                        {[
                            "Understand the UID/GID relationship",
                            "Create, modify, and delete user accounts",
                            "Manage group memberships for team collaboration",
                            "Identify the three core security files in /etc/",
                            "Explain the purpose of the superuser (root)",
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* RESOURCES */}
                <Box sx={{ mb: 4 }}>
                    <SectionTitle icon={<LinkIcon />} label="Technical Resources" />
                    <ResourceLink href="https://linuxize.com/post/how-to-add-and-delete-users-on-ubuntu-20-04/" label="User Management on Ubuntu" sub="Step-by-step guide for account lifecycle management." />
                    <ResourceLink href="https://www.geeksforgeeks.org/user-management-in-linux/" label="Linux User Management - GFG" sub="Deep dive into terminal-based administration." />
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Always use <code style={{ color: BRAND.goldBright }}>-aG</code> when adding a user to a group. The <code style={{ color: BRAND.goldBright }}>-a</code> (append) is critical; without it, you might accidentally remove the user from all their other groups!
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top Button */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab
                        size="small"
                        onClick={scrollToTop}
                        sx={{
                            bgcolor: BRAND.gold,
                            "&:hover": { bgcolor: BRAND.goldBright },
                            boxShadow: `0 0 10px ${BRAND.goldGlow}`
                        }}
                    >
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
